import { useEffect, useState } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { FileText, Plus } from 'lucide-react'
import { Button } from '@/ui/components/common/Button'
import { Card, CardContent } from '@/ui/components/common/Card'
import {
  useDeleteInvestigation,
  useGetAllInvestigations,
} from '@/application/services/investigation.service'
import { useGetMyInvestigations } from '@/application/services/user.service'
import { useAuthStore } from '@/application/stores/auth.store'
import { classNames } from '@/ui/utils/main'
import { CategoryFilterSelect } from '@/ui/components/forms/CategoryFilterSelect'
import { InvestigationTile } from './InvestigationTile'
import { Paginator } from './Paginator'
import { SuggestionsSection } from './SuggestionsSection'
import { TileSkeleton } from './TileSkeleton'

const PAGE_SIZE = 10

interface InvestigationsListSectionProps {
  isPrivileged: boolean
  statusFilter: 'active' | 'inactive' | 'pending' | 'suggestions'
  /** Category term slug from the URL search params; undefined means all. */
  category?: string
}

/**
 * Lists the investigations the current user can see (all, for privileged
 * users; their own otherwise), filtered by status. Includes the "Create"
 * action shortcut and pagination. Reads/writes statusFilter via URL search
 * params so that navigation from create/edit pages can pre-select the right
 * filter; the category filter lives in the URL the same way. Owns its own page state and delete mutation.
 */
export function InvestigationsListSection({
  isPrivileged,
  statusFilter,
  category,
}: InvestigationsListSectionProps) {
  const { t } = useTranslation()
  const { user } = useAuthStore()
  const navigate = useNavigate()

  const { data: allInvestigations, isLoading: allLoading } = useGetAllInvestigations()
  const { data: myInvestigations, isLoading: myLoading } = useGetMyInvestigations()
  const investigations = isPrivileged ? allInvestigations : myInvestigations
  const isLoading = isPrivileged ? allLoading : myLoading

  const { mutate: deleteInvestigation } = useDeleteInvestigation()
  const [page, setPage] = useState(1)

  // Reset to page 1 whenever the URL-driven filter changes
  useEffect(() => {
    setPage(1)
  }, [statusFilter, category])

  const handleFilterChange = (newStatus: 'active' | 'inactive' | 'pending' | 'suggestions') => {
    navigate({ to: '/manage/investigations', search: { statusFilter: newStatus, category } })
  }

  const handleCategoryChange = (slug: string | undefined) => {
    navigate({ to: '/manage/investigations', search: { statusFilter, category: slug } })
  }

  const filteredInvestigations =
    investigations?.filter(
      (investigation) =>
        investigation.status === statusFilter &&
        (!category || (investigation.categoryTerms ?? []).some((term) => term.slug === category)),
    ) ?? []
  const totalPages = Math.max(1, Math.ceil(filteredInvestigations.length / PAGE_SIZE))
  const paginated = filteredInvestigations.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  const handleDelete = async (entityId: string, name: string) => {
    if (!window.confirm(t('admin.inv_delete_confirm', { name }))) return
    try {
      await deleteInvestigation(entityId)
      toast.success(t('admin.inv_deleted', { name }))
      const remaining = (investigations?.length ?? 1) - 1
      const newTotal = Math.max(1, Math.ceil(remaining / PAGE_SIZE))
      if (page > newTotal) setPage(newTotal)
    } catch (error: unknown) {
      toast.error(
        t('admin.inv_delete_failed', {
          error: error instanceof Error ? error.message : String(error),
        }),
      )
    }
  }

  return (
    <div className="space-y-6">
      {/* Header — Create action */}
      {isPrivileged && (
        <div className="flex justify-end">
          <Link to="/manage/investigations/create">
            <Button size="sm" leftIcon={<Plus className="w-4 h-4" />}>
              {t('admin.form_create_submit')}
            </Button>
          </Link>
        </div>
      )}

      {/* Status filter pills + category filter (second line on small screens) */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-2 flex-wrap">
          {(['active', 'pending', 'inactive'] as const).map((status) => (
            <button
              key={status}
              onClick={() => handleFilterChange(status)}
              className={classNames(
                'px-3 py-1 rounded-full text-sm font-medium transition-colors border',
                statusFilter === status
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'border-border text-muted-foreground hover:border-primary/50 hover:text-foreground',
              )}
            >
              {status === 'active'
                ? t('admin.inv_status_active')
                : status === 'inactive'
                  ? t('admin.inv_status_inactive')
                  : t('admin.inv_status_pending')}
            </button>
          ))}

          {/* Suggestions tab — privileged users only */}
          {isPrivileged && (
            <button
              onClick={() => handleFilterChange('suggestions')}
              className={classNames(
                'px-3 py-1 rounded-full text-sm font-medium transition-colors border',
                statusFilter === 'suggestions'
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'border-border text-muted-foreground hover:border-primary/50 hover:text-foreground',
              )}
            >
              {t('admin.nav_suggestions')}
            </button>
          )}
        </div>

        {statusFilter !== 'suggestions' && (
          <CategoryFilterSelect
            className="w-full md:w-60"
            value={category}
            onChange={handleCategoryChange}
          />
        )}
      </div>

      {/* Suggestions tab content */}
      {statusFilter === 'suggestions' ? (
        <SuggestionsSection />
      ) : isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_item, index) => (
            <TileSkeleton key={index} />
          ))}
        </div>
      ) : filteredInvestigations.length > 0 ? (
        <>
          <div className="space-y-3">
            {paginated.map((investigation) => (
              <InvestigationTile
                key={investigation.id}
                investigation={investigation}
                onDelete={() => handleDelete(investigation.id, investigation.name)}
                canEdit={isPrivileged || investigation.authorId === user?.id}
                canDelete={isPrivileged}
                canToggle={isPrivileged}
                onStatusChange={(newStatus) =>
                  navigate({
                    to: '/manage/investigations',
                    search: { statusFilter: newStatus, category },
                  })
                }
              />
            ))}
          </div>
          <Paginator page={page} total={totalPages} onChange={setPage} />
        </>
      ) : (
        <Card>
          <CardContent className="text-center py-12">
            <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-semibold mb-2">{t('admin.inv_empty_title')}</h3>
            <p className="text-muted-foreground text-sm mb-6">{t('admin.inv_empty_body')}</p>
            {isPrivileged && (
              <Link to="/manage/investigations/create">
                <Button leftIcon={<Plus className="w-4 h-4" />}>
                  {t('admin.inv_empty_button')}
                </Button>
              </Link>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}
